import { Alert, Button, Card, Checkbox, Form, Input, Select, message } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type FormValues = {
  framework_code: string;
  name: string;
  kind: string;
  authority_name?: string;
  description?: string;
  is_readiness_only?: boolean;
  company_scoped?: boolean;
};

export default function StandardsFormPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.standards.manage') || user?.permissions?.includes('education.admin')
  );

  const create = useMutation({
    mutationFn: (values: FormValues) => api.post<{ id: string }>('/education/standards/frameworks', values),
    onSuccess: (res) => {
      message.success(t('standardFrameworkCreated'));
      const id = res.data?.id;
      navigate(id ? `/education/standards/${id}` : '/education/standards');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (!canManage) {
    return (
      <EducationPageChrome title={t('newStandardFramework')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={t('newStandardFramework')}
      description={t('standardsIntro')}
      extra={<Button onClick={() => navigate('/education/standards')}>{t('standards')}</Button>}
    >
      <Card size="small" style={{ maxWidth: 640 }}>
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('standardsReadinessHint')} />
        <Form layout="vertical" initialValues={{ kind: 'board' }} onFinish={(values) => create.mutate(values)}>
          <Form.Item name="framework_code" label={t('frameworkCode')} rules={[{ required: true }]}>
            <Input maxLength={40} placeholder="e.g. STATE_MH" />
          </Form.Item>
          <Form.Item name="name" label={t('name')} rules={[{ required: true }]}>
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="kind" label={t('frameworkKind')} rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'national', label: t('kindNational') },
                { value: 'board', label: t('kindBoard') },
                { value: 'state', label: t('kindState') },
                { value: 'quality', label: t('kindQuality') },
                { value: 'readiness', label: t('kindReadiness') },
              ]}
            />
          </Form.Item>
          <Form.Item name="authority_name" label={t('authorityName')}>
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="description" label={t('detail')}>
            <Input.TextArea rows={3} maxLength={1000} />
          </Form.Item>
          <Form.Item name="is_readiness_only" valuePropName="checked">
            <Checkbox>{t('readinessOnly')}</Checkbox>
          </Form.Item>
          <Form.Item name="company_scoped" valuePropName="checked">
            <Checkbox>{t('companyScopedFramework')}</Checkbox>
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={create.isPending}>
            {t('save')}
          </Button>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
